import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { accountingAPI } from '../../services/api';

const FinancialSummary = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [startDate, setStartDate] = useState(new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    loadSummary();
  }, [startDate, endDate]);

  const loadSummary = async () => {
    setLoading(true);
    try {
      const response = await accountingAPI.getFinancialSummary({
        start_date: startDate,
        end_date: endDate,
      });
      setData(response.data.data);
    } catch (error) {
      console.error('Error loading financial summary:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const calculatePercentage = (value, total) => {
    return total > 0 ? ((value / total) * 100).toFixed(1) : 0;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-3xl font-bold text-gray-900">
          {t('accounting.financialSummary')}
        </h2>
        <p className="text-gray-600 mt-1">{t('accounting.profitLossAnalysis')}</p>
      </div>

      {/* Date Range */}
      <div className="bg-white rounded-lg shadow p-4 flex gap-4 items-end">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('common.startDate')}
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('common.endDate')}
          </label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {data && (
        <>
          {/* Financial Summary Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="p-6 space-y-4">
              {/* Gross Subtotal */}
              <div className="flex items-center justify-between pb-4 border-b-2 border-gray-200">
                <div>
                  <p className="text-sm text-gray-600">{t('accounting.grossSubtotal')}</p>
                  <p className="text-xs text-gray-500 mt-1">(Sum of all order items before discounts)</p>
                </div>
                <p className="text-3xl font-bold text-blue-900">
                  {t('common.currency')} {data.gross_subtotal?.toFixed(2)}
                </p>
              </div>

              {/* Less Item Discounts */}
              <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <span className="text-red-500 text-xl">-</span>
                  <div>
                    <p className="text-sm text-gray-600">{t('accounting.lessItemDiscounts')}</p>
                    <p className="text-xs text-gray-500 mt-1">{calculatePercentage(data.less_item_discounts, data.gross_subtotal)}% of gross</p>
                  </div>
                </div>
                <p className="text-2xl font-bold text-red-600">
                  -{t('common.currency')} {data.less_item_discounts?.toFixed(2)}
                </p>
              </div>

              {/* Less Order Discounts */}
              <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <span className="text-red-500 text-xl">-</span>
                  <div>
                    <p className="text-sm text-gray-600">{t('accounting.lessOrderDiscounts')}</p>
                    <p className="text-xs text-gray-500 mt-1">{calculatePercentage(data.less_order_discounts, data.gross_subtotal)}% of gross</p>
                  </div>
                </div>
                <p className="text-2xl font-bold text-red-600">
                  -{t('common.currency')} {data.less_order_discounts?.toFixed(2)}
                </p>
              </div>

              {/* Subtotal After Discounts */}
              <div className="flex items-center justify-between pb-4 border-b-2 border-gray-300 bg-gray-50 p-4 rounded">
                <div>
                  <p className="text-sm font-semibold text-gray-700">{t('accounting.subtotalAfterDiscounts')}</p>
                  <p className="text-xs text-gray-500 mt-1">(Subject to VAT and Fees)</p>
                </div>
                <p className="text-3xl font-bold text-gray-900">
                  {t('common.currency')} {data.subtotal_after_discounts?.toFixed(2)}
                </p>
              </div>

              {/* Plus VAT */}
              <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <span className="text-yellow-600 text-xl">+</span>
                  <div>
                    <p className="text-sm text-gray-600">{t('accounting.plusVAT')}</p>
                    <p className="text-xs text-gray-500 mt-1">{calculatePercentage(data.plus_vat_collected, data.subtotal_after_discounts)}% of subtotal</p>
                  </div>
                </div>
                <p className="text-2xl font-bold text-yellow-600">
                  +{t('common.currency')} {data.plus_vat_collected?.toFixed(2)}
                </p>
              </div>

              {/* Plus Fees */}
              <div className="flex items-center justify-between pb-4 border-b-2 border-gray-300">
                <div className="flex items-center gap-2">
                  <span className="text-green-600 text-xl">+</span>
                  <div>
                    <p className="text-sm text-gray-600">{t('accounting.plusFees')}</p>
                    <p className="text-xs text-gray-500 mt-1">{calculatePercentage(data.plus_fees_charged, data.gross_subtotal)}% of gross</p>
                  </div>
                </div>
                <p className="text-2xl font-bold text-green-600">
                  +{t('common.currency')} {data.plus_fees_charged?.toFixed(2)}
                </p>
              </div>

              {/* Net Revenue */}
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg">
                <div>
                  <p className="text-sm font-bold text-blue-900">{t('accounting.netRevenue')}</p>
                  <p className="text-xs text-blue-700 mt-1">Final revenue after all adjustments</p>
                </div>
                <p className="text-4xl font-bold text-blue-900">
                  {t('common.currency')} {data.net_revenue?.toFixed(2)}
                </p>
              </div>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 rounded-lg p-6">
              <p className="text-sm font-medium text-blue-600">Total Orders</p>
              <p className="text-4xl font-bold text-blue-900 mt-2">{data.total_orders}</p>
              <p className="text-xs text-blue-600 mt-2">Period: {data.period_start} to {data.period_end}</p>
            </div>

            {/* Breakdown */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-6">
              <p className="text-sm font-medium text-purple-600">Total Discounts</p>
              <p className="text-2xl font-bold text-purple-900 mt-2">
                -{t('common.currency')} {(data.less_item_discounts + data.less_order_discounts)?.toFixed(2)}
              </p>
              <p className="text-xs text-purple-600 mt-2">
                {calculatePercentage(data.less_item_discounts + data.less_order_discounts, data.gross_subtotal)}% reduction
              </p>
            </div>

            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-lg p-6">
              <p className="text-sm font-medium text-emerald-600">Total Value Added</p>
              <p className="text-2xl font-bold text-emerald-900 mt-2">
                +{t('common.currency')} {(data.plus_vat_collected + data.plus_fees_charged)?.toFixed(2)}
              </p>
              <p className="text-xs text-emerald-600 mt-2">
                VAT + Fees combined
              </p>
            </div>
          </div>

          {/* Percentage Breakdown */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Percentage Breakdown</h3>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Discounts Impact</span>
                  <span className="text-sm font-semibold text-gray-900">
                    -{calculatePercentage(data.less_item_discounts + data.less_order_discounts, data.gross_subtotal)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-red-500 h-2 rounded-full"
                    style={{ width: `${calculatePercentage(data.less_item_discounts + data.less_order_discounts, data.gross_subtotal)}%` }}
                  ></div>
                </div>
              </div>

              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">VAT Collection</span>
                  <span className="text-sm font-semibold text-gray-900">
                    +{calculatePercentage(data.plus_vat_collected, data.gross_subtotal)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-yellow-500 h-2 rounded-full"
                    style={{ width: `${calculatePercentage(data.plus_vat_collected, data.gross_subtotal)}%` }}
                  ></div>
                </div>
              </div>

              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Fees Charged</span>
                  <span className="text-sm font-semibold text-gray-900">
                    +{calculatePercentage(data.plus_fees_charged, data.gross_subtotal)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-500 h-2 rounded-full"
                    style={{ width: `${calculatePercentage(data.plus_fees_charged, data.gross_subtotal)}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default FinancialSummary;
